/**
 * Template Library — Built-in Templates
 *
 * Built-in templates are sourced from recipe.json files in @/data/recipes.
 * Recipes are validated and converted to TemplateDefinition at import time
 * so the create dialog and empty canvas can render instantly without a
 * network fetch. The legacy upstream community template fetch has been
 * removed — a new API will replace it in a future release.
 */

import type { TemplateDefinition, TemplateManifest } from '@/types/template';
import { RECIPE_TEMPLATES, getRecipeExtras } from '@/data/recipes';

/** Built-in templates — always available synchronously. */
export const BUILTIN_TEMPLATES: TemplateDefinition[] = RECIPE_TEMPLATES;

/**
 * Curated featured template IDs shown on the empty canvas and create dialog.
 * Order matters — first 3 shown on empty canvas.
 */
export const FEATURED_TEMPLATE_IDS = [
  'receive-request-send-response', // HTTP trigger + response template — good for testing and demos
  'tool-call-uri-http',        // HTTP endpoint caller
] as const;

/** Manifest-only view of built-in templates (no workflow payload). */
export const BUILTIN_MANIFESTS: TemplateManifest[] = BUILTIN_TEMPLATES.map(
  ({ workflowDefinition: _wd, connectionsConfiguration: _cc, ...manifest }) => {
    const featuredIdx = FEATURED_TEMPLATE_IDS.indexOf(manifest.id as typeof FEATURED_TEMPLATE_IDS[number]);
    if (featuredIdx !== -1) {
      return { ...manifest, featuredOrder: featuredIdx + 1 };
    }
    // Strip any stale featuredOrder that may exist in the JSON source
    const { featuredOrder: _fo, ...rest } = manifest;
    return rest;
  }
);

/** Look up a built-in template by ID. */
export function getBuiltinTemplateById(id: string): TemplateDefinition | undefined {
  return BUILTIN_TEMPLATES.find((t) => t.id === id);
}

/** Re-export recipe extras lookup for use by template apply and template mode. */
export { getRecipeExtras };
